import React, { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useDispatch } from 'react-redux';
import { showModal } from '../../../../../../store/actions/modal';
import Icon from '../../../../../../components/Icon';
import Modal from '../../../../../../components/Modal';
import { setToLocalTimeZone, dateSpliter } from '../../../../../../helpers/dates-hanlder';
import moment from 'moment';
import {
  TriggerButton,
  ButtonIcon,
  ButtonText,
  ModalContent,
  CalendarIcon,
  Title,
  Description,
  ButtonsContainer,
  CalendarButton,
  CalendarButtonIcon,
  CalendarButtonText
} from './styles';

// Iconos para los calendarios
import googleCalendarIcon from './assets/google-calendar.svg';
import yahooIcon from './assets/yahoo.svg';
import outlookIcon from './assets/outlook.svg';
import icalIcon from './assets/ical.svg';

interface AddToCalendarProps {
  session: {
    title: string;
    session_datetime: string;
    join_url?: string;
    description?: string;
    duration?: number;
    duration_type?: string;
  };
  isVisible?: boolean;
}

const AddToCalendar: React.FC<AddToCalendarProps> = ({
  session,
  isVisible = true
}) => {
  const theme = useTheme() as { colors: { [key: string]: string } };
  const dispatch = useDispatch();

  // Memoizar la función formatDateForCalendar
  const formatDateForCalendar = useCallback((dateString: string, forGoogle: boolean = false, forOutlook: boolean = false, forICal: boolean = false) => {
    if (forICal) {
      // Para iCal, convertir a zona horaria local y luego a UTC
      const localDate = setToLocalTimeZone(dateString);
      const utcDate = moment(localDate).utc();
      return utcDate.format('YYYYMMDDTHHmmss') + 'Z';
    }

    // Convertir a zona horaria local usando el helper
    const localDate = setToLocalTimeZone(dateString);
    const momentDate = moment(localDate);

    if (forGoogle) {
      // Para Google Calendar, no agregar 'Z' ya que maneja las zonas horarias internamente
      return momentDate.format('YYYYMMDDTHHmmss');
    } else if (forOutlook) {
      // Para Outlook, usar formato ISO 8601 con zona horaria local
      return momentDate.format('YYYY-MM-DDTHH:mm:ss');
    } else {
      // Para otros calendarios, usar formato UTC
      return momentDate.format('YYYYMMDDTHHmmss') + 'Z';
    }
  }, []);

  // Memoizar la función getEndTime
  const getEndTime = useCallback((forGoogle: boolean = false, forOutlook: boolean = false, forICal: boolean = false) => {
    if (!session?.duration || !session?.duration_type) return null;

    if (forICal) {
      // Para iCal, convertir a zona horaria local y luego a UTC
      const localStartDate = setToLocalTimeZone(session?.session_datetime);
      const startMoment = moment(localStartDate).utc();
      let endMoment = moment(startMoment);

      if (session?.duration_type === 'hour') {
        endMoment = endMoment.add(session?.duration, 'hours');
      } else if (session?.duration_type === 'minute') {
        endMoment = endMoment.add(session?.duration, 'minutes');
      } else if (session?.duration_type === 'day') {
        endMoment = endMoment.add(session?.duration, 'days');
      }

      return endMoment.format('YYYYMMDDTHHmmss') + 'Z';
    }

    // Convertir a zona horaria local
    const localStartDate = setToLocalTimeZone(session?.session_datetime);
    const startMoment = moment(localStartDate);

    let endMoment = moment(startMoment);

    if (session?.duration_type === 'hour') {
      endMoment = endMoment.add(session?.duration, 'hours');
    } else if (session?.duration_type === 'minute') {
      endMoment = endMoment.add(session?.duration, 'minutes');
    } else if (session?.duration_type === 'day') {
      endMoment = endMoment.add(session?.duration, 'days');
    }

    if (forGoogle) {
      // Para Google Calendar, no agregar 'Z'
      return endMoment.format('YYYYMMDDTHHmmss');
    } else if (forOutlook) {
      // Para Outlook, usar formato ISO 8601 con zona horaria local
      return endMoment.format('YYYY-MM-DDTHH:mm:ss');
    } else {
      // Para otros calendarios, usar formato UTC
      return endMoment.format('YYYYMMDDTHHmmss') + 'Z';
    }
  }, [session?.duration, session?.duration_type, session?.session_datetime]);

  // Memoizar los tiempos calculados
  const calendarTimes = useMemo(() => {
    return {
      google: {
        start: formatDateForCalendar(session?.session_datetime, true),
        end: getEndTime(true) || formatDateForCalendar(session?.session_datetime, true)
      },
      outlook: {
        start: formatDateForCalendar(session?.session_datetime, false, true),
        end: getEndTime(false, true) || formatDateForCalendar(session?.session_datetime, false, true)
      },
      ical: {
        start: formatDateForCalendar(session?.session_datetime, false, false, true),
        end: getEndTime(false, false, true) || formatDateForCalendar(session?.session_datetime, false, false, true)
      },
      other: {
        start: formatDateForCalendar(session?.session_datetime, false),
        end: getEndTime(false) || formatDateForCalendar(session?.session_datetime, false)
      }
    };
  }, [formatDateForCalendar, getEndTime, session?.session_datetime]);

  // Memoizar las URLs de calendario
  const calendarUrls = useMemo(() => {
    return {
      google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(session?.title)}&dates=${calendarTimes.google.start}/${calendarTimes.google.end}&location=${encodeURIComponent(session?.join_url || '')}`,
      yahoo: `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(session?.title)}&st=${calendarTimes.other.start}&et=${calendarTimes.other.end}&in_loc=${encodeURIComponent(session?.join_url || '')}`,
      outlook: `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(session?.title)}&startdt=${calendarTimes.outlook.start}&enddt=${calendarTimes.outlook.end}&location=${encodeURIComponent(session?.join_url || '')}`,
      ical: `data:text/calendar;charset=utf8,BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Klouser//Event//EN
BEGIN:VEVENT
UID:${Date.now()}-${Math.random().toString(36).substr(2, 9)}@klouser.com
DTSTAMP:${moment().utc().format('YYYYMMDDTHHmmss')}Z
DTSTART:${calendarTimes.ical.start}
DTEND:${calendarTimes.ical.end}
SUMMARY:${session?.title.replace(/\n/g, '\\n').replace(/,/g, '\\,')}
LOCATION:${(session?.join_url || '').replace(/\n/g, '\\n').replace(/,/g, '\\,')}
STATUS:CONFIRMED
TRANSP:OPAQUE
END:VEVENT
END:VCALENDAR`
    };
  }, [calendarTimes, session?.title, session?.join_url]);

  // Memoizar el handler de click en calendario
  const handleCalendarClick = useCallback((url: string, type: string) => {
    if (type === 'ical') {
      // Para iCal, crear un blob y descargar
      const icalContent = url.replace('data:text/calendar;charset=utf8,', '');
      const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${session?.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } else {
      console.log('Opening URL:', url);
      // Para otros calendarios, abrir en nueva ventana
      window.open(url, '_blank');
    }
  }, [session?.title]);

  // Memoizar el handler de abrir modal
  const handleOpenModal = useCallback(() => {
    dispatch(showModal('add-to-calendar-modal'));
  }, [dispatch]);

  // Early return después de todos los hooks
  if (!isVisible) return null;

  return (
    <>
      {/* Botón trigger */}
      <TriggerButton onClick={handleOpenModal}>
        {/* <ButtonIcon>
          <Icon icon={icalIcon} size="20px" color="#666" />
        </ButtonIcon> */}
        <ButtonText>Añadir al calendario</ButtonText>
      </TriggerButton>

      {/* Modal */}
      <Modal
        name="add-to-calendar-modal"
        title="Añadir al calendario"
        showClose={true}
        width="300"
      >
        <ModalContent>
          <Description>
            Puedes hacer clic en uno de los botones para añadir el evento a tu calendario.
          </Description>

          <ButtonsContainer>
            <CalendarButton
              onClick={() => handleCalendarClick(calendarUrls.google, 'google')}
              $color="#e3f2fd"
              $textColor="#1976d2"
            >
              <CalendarButtonIcon>
                <Icon icon={googleCalendarIcon} size="20px" color="#1976d2" />
              </CalendarButtonIcon>
              <CalendarButtonText>Google Calendar</CalendarButtonText>
            </CalendarButton>

            <CalendarButton
              onClick={() => handleCalendarClick(calendarUrls.yahoo, 'yahoo')}
              $color="#f3e5f5"
              $textColor="#7b1fa2"
            >
              <CalendarButtonIcon>
                <Icon icon={yahooIcon} size="20px" color="#7b1fa2" />
              </CalendarButtonIcon>
              <CalendarButtonText>Yahoo</CalendarButtonText>
            </CalendarButton>

            <CalendarButton
              onClick={() => handleCalendarClick(calendarUrls.outlook, 'outlook')}
              $color="#fff3e0"
              $textColor="#f57c00"
            >
              <CalendarButtonIcon>
                <Icon icon={outlookIcon} size="20px" color="#f57c00" />
              </CalendarButtonIcon>
              <CalendarButtonText>Outlook.com</CalendarButtonText>
            </CalendarButton>

            <CalendarButton
              onClick={() => handleCalendarClick(calendarUrls.ical, 'ical')}
              $color="#f5f5f5"
              $textColor="#424242"
            >
              <CalendarButtonIcon>
                <Icon icon={icalIcon} size="20px" color="#424242" />
              </CalendarButtonIcon>
              <CalendarButtonText>iCal (Apple / Outlook)</CalendarButtonText>
            </CalendarButton>
          </ButtonsContainer>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddToCalendar;